<template>
	<div class="sidebar-footer" :class="{ collapsed }">
		<div v-if="canSwitchOrganization" class="org-switcher px-2 pt-1 pb-2 border-b border-[var(--border-color)] mb-1">
			<label class="text-xs opacity-70 mb-1.5 block truncate" :title="t('menu.switch_organization_label')">
				{{ t("menu.switch_organization_label") }}
			</label>
			<Select
				:key="orgSelectResetKey"
				:model-value="currentOrganizationId ?? undefined"
				:options="organizationSelectOptions"
				option-label="label"
				option-value="value"
				class="w-full text-sm org-switcher-select"
				:disabled="switchingOrg"
				@update:model-value="onOrganizationSelect"
			/>
		</div>
		<n-menu :options="menuOptions" :collapsed="collapsed" :collapsed-width="collapsedWidth" />
		<Dialog
			v-model:visible="confirmSwitchVisible"
			modal
			:header="t('menu.switch_organization_confirm_title')"
			:style="{ width: 'min(420px, 94vw)' }"
			:closable="!switchingOrg"
			:dismissable-mask="!switchingOrg"
			@hide="onOrganizationSwitchDialogHide"
		>
			<p class="text-sm m-0 leading-relaxed">
				{{ t("menu.switch_organization_confirm_message", { name: pendingOrganizationName }) }}
			</p>
			<template #footer>
				<Button :label="t('dialogs.cancel')" severity="secondary" text :disabled="switchingOrg" @click="cancelOrganizationSwitch" />
				<Button
					:label="t('menu.switch_organization_confirm')"
					icon="pi pi-check"
					severity="primary"
					:loading="switchingOrg"
					@click="confirmOrganizationSwitch"
				/>
			</template>
		</Dialog>
	</div>
</template>

<script lang="ts" setup>
import { computed, h, ref, toRefs, watch } from "vue"
import { NMenu } from "naive-ui"
import Select from "primevue/select"
import Dialog from "primevue/dialog"
import Button from "primevue/button"
import { useThemeStore } from "@/stores/theme"
import { useAuthStore } from "@/stores/auth"
import { renderIcon } from "@/utils"
import { useRouter } from "vue-router"
import { useNotificationStore } from "@/stores/apps/notification"
import { useChangelogStore } from "@/stores/apps/changelog"
import BellNotificationIcon from "@/customComponents/BellNotificationIcon.vue"
import { useStoreI18n } from "@/composables/useStoreI18n"
import { axiosToken } from "@/utils/http"
import { useToast } from "primevue/usetoast"

const LogoutIcon = "ion:log-out-outline"
const UserIcon = "ion:person-outline"

const storeNotification = useNotificationStore()
const storeChangelog = useChangelogStore()
const storeAuth = useAuthStore()

defineOptions({
	name: "SidebarFooter"
})
const props = withDefaults(
	defineProps<{
		collapsed?: boolean
	}>(),
	{ collapsed: false }
)
const { collapsed } = toRefs(props)
const { t } = useStoreI18n()
const toast = useToast()

const confirmSwitchVisible = ref(false)
const pendingOrganizationId = ref<number | null>(null)
const pendingOrganizationName = ref("")
const switchingOrg = ref(false)
/** Remount Select when the confirm dialog closes without a successful switch (PrimeVue keeps internal selection otherwise). */
const orgSelectResetKey = ref(0)

function orgProfileOrganizationId(profile: Record<string, unknown>): number | undefined {
	const o = profile?.organization
	if (o == null) return undefined
	if (typeof o === "object" && o !== null && "id" in o) {
		const id = (o as { id: unknown }).id
		return typeof id === "number" ? id : undefined
	}
	return typeof o === "number" ? o : undefined
}

function orgProfileLabel(profile: Record<string, unknown>): string {
	const detail = profile?.organization_detail as { name?: string } | undefined
	if (detail?.name) return detail.name
	const id = orgProfileOrganizationId(profile)
	const fu = storeAuth.full_user as { organization?: { id?: number; name?: string } }
	if (id != null && fu?.organization?.id === id && fu.organization.name) return fu.organization.name
	return id != null ? `Organization #${id}` : ""
}

const allOrganizationProfiles = computed(() => {
	const raw = (storeAuth.full_user as { all_organizations?: unknown[] })?.all_organizations
	return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
})

const canSwitchOrganization = computed(() => allOrganizationProfiles.value.length > 1)

const organizationSelectOptions = computed(() => {
	const seen = new Set<number>()
	const out: { label: string; value: number }[] = []
	for (const p of allOrganizationProfiles.value) {
		const id = orgProfileOrganizationId(p)
		if (id == null || seen.has(id)) continue
		seen.add(id)
		out.push({ label: orgProfileLabel(p), value: id })
	}
	return out.sort((a, b) => a.label.localeCompare(b.label))
})

const currentOrganizationId = computed(() => (storeAuth.full_user as { organization?: { id?: number } })?.organization?.id ?? null)

function onOrganizationSelect(orgId: number | null) {
	if (orgId == null) return
	const current = currentOrganizationId.value
	if (current == null || orgId === current) return
	const opt = organizationSelectOptions.value.find(o => o.value === orgId)
	pendingOrganizationId.value = orgId
	pendingOrganizationName.value = opt?.label ?? `#${orgId}`
	confirmSwitchVisible.value = true
}

function onOrganizationSwitchDialogHide() {
	if (switchingOrg.value) return
	pendingOrganizationId.value = null
	pendingOrganizationName.value = ""
	orgSelectResetKey.value++
}

function cancelOrganizationSwitch() {
	confirmSwitchVisible.value = false
}

async function confirmOrganizationSwitch() {
	const id = pendingOrganizationId.value
	if (id == null) return
	switchingOrg.value = true
	try {
		await axiosToken("post", "users/user/switch-organization/", { organization_id: id })
		window.location.href = "/"
	} catch (e: unknown) {
		console.error(e)
		const raw = (e as { response?: { data?: { error?: unknown } } })?.response?.data?.error
		let msg: string | undefined
		if (typeof raw === "string") msg = raw
		else if (Array.isArray(raw)) msg = raw.map(String).join(", ")
		toast.add({
			severity: "error",
			summary: msg || t("menu.switch_organization_error"),
			life: 5000
		})
		switchingOrg.value = false
		cancelOrganizationSwitch()
	}
}

const menuOptions = ref([
	{
		label: () =>
		h(
			'button', // Using a button instead of RouterLink
			{
				class: 'menu-button' // You can style the button as needed
			},
			storeAuth.user?.email || 'User' // Display email or fallback to 'User'
		),
		onClick: openUserProfile, // You'll need to implement this function
		key: 'User',
		icon: renderIcon(UserIcon)
	},
	{
		label: () =>
		h(
			'button', // Using a button instead of RouterLink
			{
				class: 'menu-button' // You can style the button as needed
			},
			t("menu.notifications")
		),
		onClick: openNotifications,
		key: 'Notification',
		icon: renderIcon(
			h(
				BellNotificationIcon,
				{
					newNotification: storeNotification.getUnreadNotifications(), 
					iconName: 'ion:notifications-outline', 
				}
			)
		) // Use the icon you want
	},
	{
		label: () =>
		h(
			'button',
			{
				class: 'menu-button'
			},
			t("menu.changelog")
		),
		onClick: openChangelog,
		key: 'Changelog',
		icon: renderIcon(
			h(
				BellNotificationIcon,
				{
					newNotification: storeChangelog.hasUnread,
					iconName: 'ion:sparkles-outline',
				}
			)
		)
	},
	{
		label: () =>
		h(
			'button', // Using a button instead of RouterLink
			{
				class: 'menu-button' // You can style the button as needed
			},
			t("menu.logout")
		),
		onClick: logout,
		key: 'Logout',
		icon: renderIcon(LogoutIcon) // Use the icon you want
	}
])
watch(storeNotification.getUnreadNotifications, () => {
	menuOptions.value[1].icon = renderIcon(
		h(
			BellNotificationIcon, { newNotification: storeNotification.getUnreadNotifications() }
		)
	)
})
watch(() => storeChangelog.hasUnread, () => {
	menuOptions.value[2].icon = renderIcon(
		h(
			BellNotificationIcon,
			{
				newNotification: storeChangelog.hasUnread,
				iconName: 'ion:sparkles-outline',
			}
		)
	)
})

const themeStore = useThemeStore()

const collapsedWidth = computed<number>(() => themeStore.sidebar.closeWidth - 16)
const router = useRouter()
function logout(e?: MouseEvent) {
	storeAuth.setLogout()
    router.push("/login")
	return e
}

function openNotifications() {
	const notificationStore = useNotificationStore()
	storeChangelog.fetchUnread().catch(() => {})
	notificationStore.showDrawer = !notificationStore.showDrawer
}

function openChangelog() {
	router.push({ name: "Changelog" })
}

function openUserProfile() {
	router.push('/user')
}
</script>

<style lang="scss" scoped>
.sidebar-footer {
	margin: 8px;
	background-color: var(--bg-body);
	border-radius: var(--border-radius);
	padding: 3px 0;
	transition: all 0.3s;

	:deep() {
		.n-menu {
			.n-menu-item-content.n-menu-item-content--selected::before {
				background-color: transparent !important;
			}
		}
	}
}
</style>